// React imports
import React from 'react';

export interface UseItemSelectionConfig<T> {
    /** All available items */
    items: T[];
    /** Currently selected items */
    selectedItems: T[];
    /** Callback when selected items change */
    onSelectedItemsChange: (items: T[]) => void;
    /** Filter function to search items */
    filterFn: (item: T, query: string) => boolean;
}

export interface UseItemSelectionReturn<T> {
    /** Temporary selected items (staged until Apply is clicked) */
    tempSelectedItems: T[];
    /** Current search query */
    searchQuery: string;
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Filtered items based on search query */
    filteredItems: T[];
    /** Set temp selected items */
    setTempSelectedItems: (items: T[]) => void;
    /** Set search query */
    setSearchQuery: (query: string) => void;
    /** Open the drawer */
    openDrawer: () => void;
    /** Close the drawer */
    closeDrawer: () => void;
    /** Handle apply action (commit temp selections) */
    handleApply: () => void;
    /** Handle cancel action (revert temp selections) */
    handleCancel: () => void;
}

export const useItemSelection = <T>({
    items,
    selectedItems,
    onSelectedItemsChange,
    filterFn
}: UseItemSelectionConfig<T>): UseItemSelectionReturn<T> => {
    const [tempSelectedItems, setTempSelectedItems] = React.useState<T[]>([]);
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [isOpen, setIsOpen] = React.useState<boolean>(false);

    // Keep a ref so openDrawer and handleCancel always read the latest selectedItems
    // without being recreated on every render (eliminates stale closure risk)
    const selectedItemsRef = React.useRef(selectedItems);
    selectedItemsRef.current = selectedItems;

    const filteredItems = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return items;
        }
        return items.filter(item => filterFn(item, searchQuery));
    }, [items, searchQuery, filterFn]);

    const openDrawer = React.useCallback(() => {
        setTempSelectedItems(selectedItemsRef.current);
        setIsOpen(true);
    }, []);

    const closeDrawer = React.useCallback(() => {
        setSearchQuery('');
        setIsOpen(false);
    }, []);

    const handleApply = React.useCallback(() => {
        onSelectedItemsChange(tempSelectedItems);
        closeDrawer();
    }, [tempSelectedItems, onSelectedItemsChange, closeDrawer]);

    const handleCancel = React.useCallback(() => {
        setTempSelectedItems(selectedItemsRef.current);
        closeDrawer();
    }, [closeDrawer]);

    return {
        tempSelectedItems,
        searchQuery,
        isOpen,
        filteredItems,
        setTempSelectedItems,
        setSearchQuery,
        openDrawer,
        closeDrawer,
        handleApply,
        handleCancel
    };
};
